import React, { useState, useCallback } from 'react';
import { Loader2, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Button } from '../ui/button';
import { useAvailability, useFirstOpenDate } from '../../hooks/useBookingData';
import { addDays, startOfDay } from 'date-fns';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';

const BUSINESS_TIMEZONE = 'America/Denver'; // MST/MDT — matches Stefen's Square calendar

// Build an ISO range covering the FULL local-business day for the given date.
const businessDayWindow = (date) => {
  const localYmd = formatInTimeZone(date, BUSINESS_TIMEZONE, 'yyyy-MM-dd');
  const startUtc = fromZonedTime(`${localYmd}T00:00:00`, BUSINESS_TIMEZONE);
  const endUtc = fromZonedTime(`${localYmd}T23:59:59`, BUSINESS_TIMEZONE);
  return { start_at: startUtc.toISOString(), end_at: endUtc.toISOString() };
};

const formatSlotTime = (iso) =>
  formatInTimeZone(new Date(iso), BUSINESS_TIMEZONE, 'h:mm a');

export const StepDateTime = ({ service, onSelect }) => {
  const [userDate, setDate] = useState(null);
  const firstOpen = useFirstOpenDate(service?.variation_id, BUSINESS_TIMEZONE);
  const [fallbackDate] = useState(() => addDays(new Date(`${formatInTimeZone(new Date(), BUSINESS_TIMEZONE, 'yyyy-MM-dd')}T12:00:00`), 1));
  const date = userDate || firstOpen.date || (firstOpen.loading ? null : fallbackDate);
  const [picked, setPicked] = useState(null);

  // Memoize the day-window function reference so the availability hook only
  // re-runs when the date or service actually changes.
  const dayWindow = useCallback((d) => businessDayWindow(d), []);
  const { slots, loading, error } = useAvailability(service?.variation_id, date, dayWindow);

  const handleDateChange = (d) => {
    if (d) {
      setDate(d);
      setPicked(null);
    }
  };

  const continueWithSlot = () => {
    if (!picked) return;
    onSelect({
      start_at: picked.start_at,
      team_member_id: picked.team_member_id,
      duration_minutes: picked.duration_minutes || service.duration_minutes,
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="flex items-center gap-2 text-neutral-700 text-sm font-semibold mb-3 uppercase tracking-wider">
          <CalendarIcon className="w-4 h-4 text-red-600" />
          Pick a Date
        </label>
        <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-2">
          <Calendar
            key={firstOpen.loading ? 'loading' : 'ready'}
            mode="single"
            selected={date}
            defaultMonth={date || undefined}
            onSelect={handleDateChange}
            disabled={(d) => d < startOfDay(new Date())}
            className="text-neutral-900"
            data-testid="booking-calendar"
          />
        </div>
        <p className="text-neutral-500 text-xs mt-2 text-center">
          All times shown in Albuquerque (Mountain Time)
        </p>
      </div>

      <div>
        <label className="flex items-center gap-2 text-neutral-700 text-sm font-semibold mb-3 uppercase tracking-wider">
          <Clock className="w-4 h-4 text-red-600" />
          Available Times
        </label>
        <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-4 min-h-[280px]">
          {loading && (
            <div className="flex items-center justify-center h-40 text-neutral-600">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading times…
            </div>
          )}
          {!loading && error && (
            <div className="text-red-600 text-sm" data-testid="availability-error">{error}</div>
          )}
          {!loading && !error && slots.length === 0 && (
            <p className="text-neutral-600 text-sm">No openings on this day. Please pick another date.</p>
          )}
          {!loading && !error && slots.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[320px] overflow-y-auto">
              {slots.map((slot) => {
                const isSelected = picked?.start_at === slot.start_at && picked?.team_member_id === slot.team_member_id;
                return (
                  <button
                    key={`${slot.start_at}-${slot.team_member_id}`}
                    type="button"
                    onClick={() => setPicked(slot)}
                    data-testid={`slot-${slot.start_at}`}
                    className={`px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                      isSelected
                        ? 'bg-red-600 border-red-600 text-white'
                        : 'bg-white border-neutral-300 text-neutral-800 hover:border-red-600 hover:text-red-600'
                    }`}
                  >
                    {formatSlotTime(slot.start_at)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <Button
          type="button"
          disabled={!picked}
          onClick={continueWithSlot}
          className="w-full mt-4 bg-red-600 hover:bg-red-700 disabled:bg-neutral-200 disabled:text-neutral-500"
          data-testid="step-datetime-continue"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
